/**
 * CR070: Flyer Diagnostics Panel (Refactored from FlyerDiagnosticsPanelV2)
 * - Uses shared merchant list from merchant_sources table
 * - Improved error display with collapsible details
 * - No longer wrapped in Collapsible (standalone page component)
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown, ChevronUp, Loader2, FileText, Bot, CheckCircle2, XCircle, AlertCircle, Search, Upload, File, X, CreditCard, Trash2, AlertTriangle, Info } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { ScanSettingsPanelV2, type ScanSettingsV2 } from './ScanSettingsPanelV2';
import { PageListView } from './PageListView';
import { PageDetailView } from './PageDetailView';
import { UnclassifiedQueue } from './UnclassifiedQueue';
import { DEFAULT_CANDIDATE_SCORING_CONFIG } from '@/lib/flyer-pipeline-v2/candidateScoringEngine';
import { savePageResults, saveNormalizedDeals, updateRunProgress } from '@/lib/flyer-pipeline-v2/pipelineRunner';
import { runFlyerExtraction, createConfigFromDiagnosticsSettings, type FlyerExtractionConfig } from '@/lib/flyer-pipeline-v2/extractionEngine';
import type { ScanPageV2, NormalizedDeal } from '@/lib/flyer-pipeline-v2/types';
import { toast } from 'sonner';
// CR086: Static import of PDF.js with CDN worker pre-configured
import { getPdfjs } from "@/lib/pdfjs-config";
import { DealGiftCard } from '@/components/deals/DealGiftCard';
import { mapNormalizedDealToUIDeal, type NormalizedDealRunContext } from '@/lib/flyer-pipeline-v2/mapNormalizedDealToUIDeal';
import { useMerchantSources } from '@/hooks/useMerchantSources';

interface RunSummary {
  id: string;
  merchant_name: string;
  status: string;
  pages_detected: number;
  pages_ocr_completed: number;
  pages_candidate: number;
  pages_ai_scanned: number;
  deals_detected_count: number;
  created_at: string;
  primary_ocr_engine: string;
  ai_page_scan_enabled: boolean;
  error_message?: string;
  error_details?: Record<string, unknown>;
}

interface PageData {
  id: string;
  page_number: number;
  image_url: string;
  thumbnail_url?: string;
  primary_ocr_score_total: number;
  primary_ocr_engine: string;
  candidate_for_ai: boolean;
  candidate_score: number;
  candidate_threshold: number;
  ai_status: string;
  deals_count: number;
}

// CR070: Error display component with collapsible details
function RunErrorCard({ run }: { run: RunSummary }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (run.status !== 'failed' && run.status !== 'error') {
    return null;
  }
  
  // Parse error details to extract provider/step info
  const errorDetails = run.error_details || {};
  const provider = (errorDetails.provider as string) || 'Unknown';
  const errorCode = (errorDetails.error_code as string) || (errorDetails.code as string) || '';
  const step = (errorDetails.step as string) || (errorDetails.phase as string) || '';
  const rawMessage = run.error_message || 'An unknown error occurred';
  
  // Determine if it's a Firecrawl scrape error
  const isFirecrawlError = rawMessage.toLowerCase().includes('firecrawl') || 
    errorCode.includes('SCRAPE') || 
    provider.toLowerCase() === 'firecrawl';
  
  // Generate helpful hints based on error type
  const getHint = () => {
    if (isFirecrawlError || errorCode.includes('ELEMENT_NOT_FOUND') || errorCode.includes('SCRAPE_ACTION_ERROR')) {
      return "This usually means the selector/action in the capture script didn't find the expected element. Try: (1) run again, (2) switch capture strategy, or (3) use Upload PDF if the site blocks automation.";
    }
    if (errorCode.includes('TIMEOUT')) {
      return "The operation timed out. Try running again or check if the source URL is accessible.";
    }
    if (errorCode.includes('RATE_LIMIT')) {
      return "Rate limited by the provider. Wait a few minutes before trying again.";
    }
    return "Check the raw error details below for more information.";
  };
  
  return (
    <div className="mt-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {step && (
              <Badge variant="outline" className="text-xs">
                Step: {step}
              </Badge>
            )}
            {provider !== 'Unknown' && (
              <Badge variant="outline" className="text-xs">
                Provider: {provider}
              </Badge>
            )}
            {errorCode && (
              <Badge variant="destructive" className="text-xs">
                {errorCode}
              </Badge>
            )}
          </div>
          
          <p className="text-sm text-destructive font-medium">
            {rawMessage.length > 150 ? `${rawMessage.slice(0, 150)}...` : rawMessage}
          </p>
          
          <div className="flex items-start gap-2 p-2 bg-muted/50 rounded text-xs text-muted-foreground">
            <Info className="h-3 w-3 shrink-0 mt-0.5" />
            <span>{getHint()}</span>
          </div>
          
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 text-xs">
                {isExpanded ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                {isExpanded ? 'Hide Details' : 'Show Raw Details'}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto whitespace-pre-wrap break-words max-h-48">
                {JSON.stringify({ message: rawMessage, ...errorDetails }, null, 2)}
              </pre>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>
    </div>
  );
}

export function FlyerDiagnosticsPanel() {
  // CR070: Use shared merchant sources hook
  const { sources, loading: merchantsLoading, getMerchantsSummary } = useMerchantSources();
  
  // Derive unique merchants from sources
  const merchants = useMemo(() => {
    const summary = getMerchantsSummary();
    // Also get first URL for each merchant
    const merchantUrls = new Map<string, string>();
    sources.forEach(s => {
      if (!merchantUrls.has(s.merchant_id) && s.source_url) {
        merchantUrls.set(s.merchant_id, s.source_url);
      }
    });
    
    return summary.map(m => ({
      id: m.merchant_id,
      name: m.merchant_name,
      url: merchantUrls.get(m.merchant_id) || '',
    }));
  }, [sources, getMerchantsSummary]);
  
  // Scan input state - default to first merchant when loaded
  const [selectedMerchantId, setSelectedMerchantId] = useState<string>('');
  const [landingUrl, setLandingUrl] = useState<string>('');
  const [isDiscovering, setIsDiscovering] = useState(false);
  
  // Initialize merchant selection when merchants load
  useEffect(() => {
    if (merchants.length > 0 && !selectedMerchantId) {
      const firstMerchant = merchants[0];
      setSelectedMerchantId(firstMerchant.id);
      setLandingUrl(firstMerchant.url);
    }
  }, [merchants, selectedMerchantId]);
  
  // PDF Upload state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Settings state
  const [settings, setSettings] = useState<ScanSettingsV2>({
    primaryOcrEngine: 'tesseract',
    aiPageScanEnabled: true,
    aiModel: 'google/gemini-3-flash-preview',
    maxAiCalls: 50,
    candidateScoringConfig: DEFAULT_CANDIDATE_SCORING_CONFIG,
  });

  // Run state
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedPageNumber, setSelectedPageNumber] = useState<number | null>(null);
  const [pages, setPages] = useState<PageData[]>([]);
  const [deals, setDeals] = useState<NormalizedDeal[]>([]);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [runToDelete, setRunToDelete] = useState<string | null>(null);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<string>('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // Tab state
  const [activeTab, setActiveTab] = useState<'runs' | 'deals' | 'unclassified'>('runs');

  // Build run context for deal mapping
  const getRunContext = useCallback((): NormalizedDealRunContext => {
    const merchant = merchants.find(m => m.id === selectedMerchantId);
    return {
      merchant_id: selectedMerchantId,
      merchant_name: merchant?.name || selectedMerchantId,
      landing_url: landingUrl,
      region: 'ON',
    };
  }, [selectedMerchantId, landingUrl, merchants]);

  // Load saved AI model preference on mount
  useEffect(() => {
    const loadSavedModel = async () => {
      const { data } = await supabase
        .from('app_config')
        .select('value_json')
        .eq('key', 'flyer_v2_ai_model')
        .single();
      
      const valueJson = data?.value_json as { model?: string } | null;
      if (valueJson?.model) {
        setSettings(prev => ({ ...prev, aiModel: valueJson.model as typeof prev.aiModel }));
      }
    };
    loadSavedModel();
  }, []);
  
  // Update URL when merchant changes
  const handleMerchantChange = (merchantId: string) => {
    setSelectedMerchantId(merchantId);
    const merchant = merchants.find(m => m.id === merchantId);
    if (merchant) {
      setLandingUrl(merchant.url);
    }
  };

  // Get current merchant name
  const getMerchantName = () => {
    const merchant = merchants.find(m => m.id === selectedMerchantId);
    return merchant?.name || selectedMerchantId;
  };

  // Load recent runs
  const loadRuns = useCallback(async () => {
    const { data, error } = await supabase
      .from('flyer_scan_runs_v2')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20);

    if (!error && data) {
      setRuns(
        data.map((r) => ({
          id: r.id,
          merchant_name: r.merchant_name,
          status: r.status,
          pages_detected: r.pages_detected ?? 0,
          pages_ocr_completed: r.pages_ocr_completed ?? 0,
          pages_candidate: r.pages_candidate ?? 0,
          pages_ai_scanned: r.pages_ai_scanned ?? 0,
          deals_detected_count: r.deals_detected_count ?? 0,
          created_at: r.created_at,
          primary_ocr_engine: r.primary_ocr_engine ?? 'tesseract',
          ai_page_scan_enabled: r.ai_page_scan_enabled ?? true,
          error_message: r.failure_reason ?? undefined,
          error_details: r.discovery_debug as Record<string, unknown> ?? undefined,
        }))
      );
    }
  }, []);

  // Load pages for selected run
  const loadPages = useCallback(async (runId: string) => {
    const { data: pagesData, error: pagesError } = await supabase
      .from('flyer_scan_pages_v2')
      .select('*')
      .eq('run_id', runId)
      .order('page_number', { ascending: true });

    if (!pagesError && pagesData) {
      // Get deal counts per page
      const { data: dealsData } = await supabase
        .from('flyer_normalized_deals')
        .select('page_number')
        .eq('run_id', runId);

      const dealCounts: Record<number, number> = {};
      dealsData?.forEach((d) => {
        dealCounts[d.page_number] = (dealCounts[d.page_number] || 0) + 1;
      });

      setPages(
        pagesData.map((p) => ({
          id: p.id,
          page_number: p.page_number,
          image_url: p.image_url ?? '',
          thumbnail_url: p.thumbnail_url ?? undefined,
          primary_ocr_score_total: Number(p.primary_ocr_score_total) || 0,
          primary_ocr_engine: p.primary_ocr_engine ?? 'unknown',
          candidate_for_ai: p.candidate_for_ai ?? false,
          candidate_score: Number(p.candidate_score) || 0,
          candidate_threshold: Number(p.candidate_threshold) || 7,
          ai_status: p.ai_status ?? 'pending',
          deals_count: dealCounts[p.page_number] || 0,
        }))
      );
    }
  }, []);

  // Load deals for selected run
  const loadDeals = useCallback(async (runId: string) => {
    const { data, error } = await supabase
      .from('flyer_normalized_deals')
      .select('*')
      .eq('run_id', runId)
      .order('page_number', { ascending: true });

    if (!error && data) {
      setDeals(
        data.map((d) => ({
          deal_id: d.id,
          run_id: d.run_id,
          page_no: d.page_number,
          status: d.status as NormalizedDeal['status'],
          deal_type: d.deal_type as NormalizedDeal['deal_type'],
          merchant: d.merchant,
          purchase: {
            brand: d.purchase_brand,
            amount: Number(d.purchase_amount) || 0,
            currency: d.purchase_currency || 'CAD',
          },
          reward: {
            type: d.reward_type as NormalizedDeal['reward']['type'],
            merchant: d.reward_merchant,
            amount: Number(d.reward_amount) || 0,
            currency: d.reward_currency || 'CAD',
          },
          validity: {
            start_date: d.validity_start_date,
            end_date: d.validity_end_date,
            raw_text: d.validity_raw_text,
          },
          confidence: Number(d.confidence) || 0,
          debug: {
            primary_score_total: Number(d.debug_primary_score_total) || 0,
            candidate_score: Number(d.debug_candidate_score) || 0,
            candidate_reason_codes: d.debug_candidate_reason_codes || [],
            ai_notes: d.debug_ai_notes || [],
          },
          override: {
            was_overridden: d.was_overridden ?? false,
            overridden_by: d.overridden_by,
            overridden_at: d.overridden_at,
            override_reason: d.override_reason,
          },
        }))
      );
    }
  }, []);

  // Delete a run
  const handleDeleteRun = useCallback(async (runId: string) => {
    setDeletingRunId(runId);
    
    try {
      const { data, error } = await supabase.functions.invoke('delete-flyer-run-v2', {
        body: { run_id: runId },
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (data?.success) {
        toast.success(`Deleted run: ${data.deleted_db_rows?.pages || 0} pages, ${data.deleted_db_rows?.deals || 0} deals`);
      } else if (data?.errors?.length > 0) {
        toast.warning(`Partial delete: ${data.errors.join(', ')}`);
      }
      
      // Clear selection if we deleted the selected run
      if (selectedRunId === runId) {
        setSelectedRunId(null);
        setPages([]);
        setDeals([]);
      }
      
      // Refresh runs list
      await loadRuns();
      
    } catch (err) {
      console.error('[CR036] Delete error:', err);
      toast.error(`Failed to delete run: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeletingRunId(null);
      setShowDeleteDialog(false);
      setRunToDelete(null);
    }
  }, [selectedRunId, loadRuns]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    if (selectedRunId) {
      loadPages(selectedRunId);
      loadDeals(selectedRunId);
    }
  }, [selectedRunId, loadPages, loadDeals]);

  // File selection handler
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedPdfFile(file);
    } else if (file) {
      toast.error('Please select a PDF file');
    }
  };

  // Start a new v2 scan: Look for approved fetch results → Create Run → Process Pages
  const handleStartScan = async () => {
    if (isProcessing || isDiscovering) return;
    
    const merchantName = getMerchantName();
    
    setIsDiscovering(true);
    setCurrentPhase('Looking for approved flyer assets...');
    
    try {
      console.log(`[CR036] Starting v2 scan for ${merchantName} at ${landingUrl}`);
      toast.info(`Looking for flyer assets for ${merchantName}...`);
      
      // Step 1: Look for approved fetch results with stored assets
      const { data: fetchResults, error: fetchError } = await supabase
        .from('flyer_fetch_results')
        .select('id, file_ref, asset_type, merchant_id, merchant_name, approved_at, status')
        .eq('merchant_id', selectedMerchantId)
        .not('approved_at', 'is', null)
        .not('file_ref', 'is', null)
        .order('approved_at', { ascending: false })
        .limit(1);
      
      if (fetchError) {
        throw new Error(`Failed to find fetch results: ${fetchError.message}`);
      }
      
      if (!fetchResults || fetchResults.length === 0) {
        throw new Error(`No approved flyer assets found for ${merchantName}. Use the Automated Fetcher to capture flyers first, then approve them.`);
      }
      
      const fetchResult = fetchResults[0];
      console.log(`[CR036] Found approved fetch result: ${fetchResult.id}, type: ${fetchResult.asset_type}`);
      setCurrentPhase(`Found approved ${fetchResult.asset_type} asset, loading pages...`);
      
      // Step 2: Get associated flyer assets (page images)
      const { data: flyerAssets, error: assetsError } = await supabase
        .from('flyer_assets')
        .select('id, storage_path, image_urls, asset_type')
        .eq('fetch_result_id', fetchResult.id)
        .order('created_at');
      
      const pageImages: { pageNumber: number; imageUrl: string; storagePath?: string }[] = [];
      
      if (flyerAssets && flyerAssets.length > 0) {
        // Use stored assets
        let pageNum = 1;
        for (const asset of flyerAssets) {
          if (asset.image_urls && Array.isArray(asset.image_urls)) {
            for (const url of asset.image_urls as string[]) {
              pageImages.push({
                pageNumber: pageNum++,
                imageUrl: url,
                storagePath: asset.storage_path,
              });
            }
          } else if (asset.storage_path) {
            // Get signed URL for the asset
            const { data: signedUrlData } = await supabase.storage
              .from('flyer-fetch-assets')
              .createSignedUrl(asset.storage_path, 3600);
            
            if (signedUrlData?.signedUrl) {
              pageImages.push({
                pageNumber: pageNum++,
                imageUrl: signedUrlData.signedUrl,
                storagePath: asset.storage_path,
              });
            }
          }
        }
      }
      
      if (pageImages.length === 0) {
        // Try to get signed URL directly from file_ref
        if (fetchResult.file_ref) {
          const { data: signedUrlData } = await supabase.storage
            .from('flyer-fetch-assets')
            .createSignedUrl(fetchResult.file_ref, 3600);
          
          if (signedUrlData?.signedUrl) {
            // This is likely a PDF - let the user know to use PDF upload instead
            throw new Error(`Found a PDF asset for ${merchantName}. Please use the "Upload PDF" option to process it, or wait for IMAGE_SET assets to be captured.`);
          }
        }
        throw new Error(`No page images found in approved assets for ${merchantName}. The asset may be a PDF - use "Upload PDF" to process it.`);
      }
      
      console.log(`[CR036] Found ${pageImages.length} pages from approved assets, creating v2 run...`);
      setCurrentPhase(`Found ${pageImages.length} pages, initializing v2 pipeline...`);
      
      // Step 3: Create a new v2 run record
      const { data: v2Run, error: v2RunError } = await supabase
        .from('flyer_scan_runs_v2')
        .insert({
          merchant_id: selectedMerchantId,
          merchant_name: merchantName,
          landing_url: landingUrl,
          region: 'ON',
          status: 'processing',
          primary_ocr_engine: settings.primaryOcrEngine,
          ai_page_scan_enabled: settings.aiPageScanEnabled,
          max_ai_calls: settings.maxAiCalls,
          pages_detected: pageImages.length,
          pages_rendered: pageImages.length,
          started_at: new Date().toISOString(),
        })
        .select()
        .single();
      
      if (v2RunError || !v2Run) {
        throw new Error(`Failed to create v2 run: ${v2RunError?.message}`);
      }
      
      setIsDiscovering(false);
      setSelectedRunId(v2Run.id);
      
      toast.success(`Found ${pageImages.length} pages from approved assets. Starting v2 pipeline...`);
      
      // Step 4: Process pages through the v2 pipeline
      await processPages(v2Run.id, pageImages);
      
    } catch (err) {
      console.error('[CR036] Scan error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to start scan');
    } finally {
      setIsDiscovering(false);
      setCurrentPhase('');
    }
  };

  // Process pages through the unified Flyer Extraction Engine
  const processPages = async (runId: string, pageImages: { pageNumber: number; imageUrl: string; storagePath?: string }[]) => {
    setIsProcessing(true);
    setProgress({ current: 0, total: pageImages.length });
    
    const merchantName = getMerchantName();
    
    try {
      // Create config from current UI settings
      const extractionConfig = createConfigFromDiagnosticsSettings({
        primaryOcrEngine: settings.primaryOcrEngine,
        aiPageScanEnabled: settings.aiPageScanEnabled,
        aiModel: settings.aiModel,
        maxAiCalls: settings.maxAiCalls,
        candidateScoringConfig: settings.candidateScoringConfig,
      });
      
      // Save config snapshot to run for reproducibility
      await supabase
        .from('flyer_scan_runs_v2')
        .update({
          discovery_debug: JSON.parse(JSON.stringify({ extraction_config: extractionConfig })),
        })
        .eq('id', runId);
      
      // Run the unified extraction engine
      const result = await runFlyerExtraction({
        runId,
        merchantName,
        pages: pageImages.map(p => ({
          pageNumber: p.pageNumber,
          imageUrl: p.imageUrl,
          imageStoragePath: p.storagePath,
        })),
        config: extractionConfig,
        onProgress: (phase, current, total) => {
          setProgress({ current, total });
          setCurrentPhase(`Page ${current}: ${phase}...`);
        },
      });
      
      // Persist results to database
      for (const pageResult of result.pages) {
        const pageId = await savePageResults(
          runId,
          pageResult.pageNumber,
          pageResult.imageUrl,
          pageResult.imageStoragePath || null,
          pageResult.ocr,
          pageResult.candidateScoring,
          pageResult.aiScan
        );
        
        if (pageResult.deals.length > 0) {
          await saveNormalizedDeals(pageResult.deals, pageId);
        }
      }
      
      // Finalize run with metrics
      await supabase
        .from('flyer_scan_runs_v2')
        .update({
          status: result.status === 'failed' ? 'failed' : 'completed',
          completed_at: new Date().toISOString(),
          pages_ocr_completed: result.metrics.pagesOcrCompleted,
          pages_candidate: result.metrics.candidatesFound,
          pages_ai_scanned: result.metrics.candidatesScanned,
          deals_detected_count: result.metrics.dealsExtracted,
          discovery_debug: JSON.parse(JSON.stringify({
            extraction_config: extractionConfig,
            metrics: result.metrics,
            errors: result.errors,
          })),
        })
        .eq('id', runId);
      
      if (result.status === 'failed') {
        toast.error(`Extraction failed: ${result.errors[0]?.message || 'Unknown error'}`);
      } else {
        toast.success(`Scan complete! Found ${result.metrics.dealsExtracted} deals across ${pageImages.length} pages.`);
      }
      
      // Reload data
      await loadRuns();
      await loadPages(runId);
      await loadDeals(runId);
      setActiveTab('deals');
      
    } catch (err) {
      console.error('[CR036] Pipeline error:', err);
      
      // Save error to run
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      await supabase
        .from('flyer_scan_runs_v2')
        .update({
          status: 'failed',
          failure_reason: errorMessage,
          discovery_debug: {
            phase: currentPhase,
            pages_processed: progress.current,
            error: errorMessage,
          },
        })
        .eq('id', runId);
      
      toast.error(`Pipeline failed: ${errorMessage}`);
      await loadRuns();
      
    } finally {
      setIsProcessing(false);
      setCurrentPhase('');
      setProgress({ current: 0, total: 0 });
    }
  };

  // Handle PDF upload and processing
  const handlePdfUpload = async () => {
    if (!selectedPdfFile || isUploading || isProcessing) return;
    
    const merchantName = getMerchantName();
    setIsUploading(true);
    setUploadProgress(0);
    setCurrentPhase('Loading PDF...');
    
    try {
      // Load PDF
      const arrayBuffer = await selectedPdfFile.arrayBuffer();
      const pdfjsLib = await getPdfjs();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const numPages = pdf.numPages;
      
      console.log(`[CR036] PDF loaded: ${numPages} pages`);
      setCurrentPhase(`Found ${numPages} pages, rendering...`);
      setUploadProgress(10);
      
      // Create v2 run
      const { data: v2Run, error: v2RunError } = await supabase
        .from('flyer_scan_runs_v2')
        .insert({
          merchant_id: selectedMerchantId,
          merchant_name: merchantName,
          landing_url: landingUrl || `pdf://${selectedPdfFile.name}`,
          region: 'ON',
          status: 'processing',
          primary_ocr_engine: settings.primaryOcrEngine,
          ai_page_scan_enabled: settings.aiPageScanEnabled,
          max_ai_calls: settings.maxAiCalls,
          pages_detected: numPages,
          started_at: new Date().toISOString(),
        })
        .select()
        .single();
      
      if (v2RunError || !v2Run) {
        throw new Error(`Failed to create run: ${v2RunError?.message}`);
      }
      
      setSelectedRunId(v2Run.id);
      
      // Render pages to images and upload to storage
      const pageImages: { pageNumber: number; imageUrl: string; storagePath: string }[] = [];
      const progressPerPage = 80 / numPages;
      
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        setCurrentPhase(`Rendering page ${pageNum}/${numPages}...`);
        
        const page = await pdf.getPage(pageNum);
        const scale = 2.0; // Higher quality
        const viewport = page.getViewport({ scale });
        
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        const context = canvas.getContext('2d')!;
        await page.render({ canvasContext: context, viewport }).promise;
        
        // Convert to blob
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.9);
        });
        
        // Upload to storage
        const storagePath = `flyer-v2/${v2Run.id}/page-${pageNum.toString().padStart(3, '0')}.jpg`;
        const { error: uploadError } = await supabase.storage
          .from('flyer-fetch-assets')
          .upload(storagePath, blob, { contentType: 'image/jpeg' });
        
        if (uploadError) {
          console.warn(`[CR036] Failed to upload page ${pageNum}:`, uploadError);
          continue;
        }
        
        // Get signed URL
        const { data: signedUrlData } = await supabase.storage
          .from('flyer-fetch-assets')
          .createSignedUrl(storagePath, 3600);
        
        if (signedUrlData?.signedUrl) {
          pageImages.push({
            pageNumber: pageNum,
            imageUrl: signedUrlData.signedUrl,
            storagePath,
          });
        }
        
        setUploadProgress(10 + Math.round(progressPerPage * pageNum));
      }
      
      // Update run with rendered pages count
      await supabase
        .from('flyer_scan_runs_v2')
        .update({ pages_rendered: pageImages.length })
        .eq('id', v2Run.id);
      
      setUploadProgress(90);
      setIsUploading(false);
      setShowUploadForm(false);
      setSelectedPdfFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      toast.success(`Uploaded ${pageImages.length} pages. Starting pipeline...`);
      
      // Process through pipeline
      await processPages(v2Run.id, pageImages);
      
    } catch (err) {
      console.error('[CR036] PDF upload error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to upload PDF');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentPhase('');
    }
  };

  // Get status icon for run
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
      case 'error':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  // Get selected run data
  const selectedRun = runs.find(r => r.id === selectedRunId);

  return (
    <div className="space-y-4">
      {/* Scan Input Section */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Scan Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Merchant + URL Row */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="merchant-select" className="text-xs">Merchant</Label>
              <Select 
                value={selectedMerchantId} 
                onValueChange={handleMerchantChange} 
                disabled={isProcessing || isDiscovering || isUploading || merchantsLoading}
              >
                <SelectTrigger id="merchant-select" className="h-9">
                  <SelectValue placeholder={merchantsLoading ? "Loading merchants..." : "Select merchant"} />
                </SelectTrigger>
                <SelectContent>
                  {merchants.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {merchants.length === 0 && !merchantsLoading && (
                <p className="text-xs text-muted-foreground">
                  No merchants found. Add merchants in{' '}
                  <a href="/admin/merchant-sources" className="text-primary hover:underline">Merchant Sources</a>.
                </p>
              )}
            </div>
            <div className="flex-[2] space-y-1.5">
              <Label htmlFor="landing-url" className="text-xs">Source URL (optional for PDF)</Label>
              <Input 
                id="landing-url"
                value={landingUrl}
                onChange={(e) => setLandingUrl(e.target.value)}
                placeholder="https://..."
                className="h-9"
                disabled={isProcessing || isDiscovering || isUploading}
              />
            </div>
          </div>
          
          {/* Action Buttons Row */}
          <div className="flex flex-wrap gap-2 items-center">
            <Button 
              onClick={handleStartScan}
              disabled={isProcessing || isDiscovering || isUploading || !selectedMerchantId}
              size="sm"
            >
              {isDiscovering ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Discovering...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Discover & Scan
                </>
              )}
            </Button>
            
            <span className="text-xs text-muted-foreground">or</span>
            
            <Button 
              variant="outline"
              size="sm"
              onClick={() => setShowUploadForm(!showUploadForm)}
              disabled={isProcessing || isDiscovering || isUploading}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload PDF
            </Button>
            
            {isProcessing && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground ml-auto">
                <Loader2 className="h-3 w-3 animate-spin" />
                {currentPhase}
              </div>
            )}
          </div>
          
          {/* PDF Upload Form */}
          {showUploadForm && (
            <div className="mt-3 p-3 border border-dashed rounded-lg bg-muted/20 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <File className="h-4 w-4" />
                  Upload Flyer PDF
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowUploadForm(false);
                    setSelectedPdfFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="pdfUploadV2"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <File className="h-4 w-4 mr-2" />
                  Select PDF
                </Button>
                {selectedPdfFile && (
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    {selectedPdfFile.name} ({Math.round(selectedPdfFile.size / 1024)}KB)
                  </span>
                )}
              </div>
              
              {isUploading && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{currentPhase}</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}
              
              <Button
                size="sm"
                onClick={handlePdfUpload}
                disabled={!selectedPdfFile || isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload & Scan
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings Panel */}
      <ScanSettingsPanelV2
        settings={settings}
        onChange={setSettings}
        disabled={isProcessing || isDiscovering || isUploading}
      />

      {/* Progress Bar for Pipeline Processing */}
      {isProcessing && !isUploading && (
        <Card>
          <CardContent className="py-4 space-y-2">
            <Progress value={(progress.current / Math.max(progress.total, 1)) * 100} />
            <p className="text-xs text-muted-foreground text-center">
              {currentPhase || `${progress.current} / ${progress.total} pages`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'runs' | 'deals' | 'unclassified')}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="runs">
            <FileText className="h-4 w-4 mr-2" />
            Runs
          </TabsTrigger>
          <TabsTrigger value="deals">
            <CreditCard className="h-4 w-4 mr-2" />
            Deals
            {deals.length > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">
                {deals.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="unclassified">
            <AlertCircle className="h-4 w-4 mr-2" />
            Review
            {deals.filter((d) => d.deal_type === 'UNCLASSIFIED').length > 0 && (
              <Badge variant="destructive" className="ml-2 text-xs">
                {deals.filter((d) => d.deal_type === 'UNCLASSIFIED').length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="runs" className="space-y-4">
          {/* Run List */}
          <Card>
            <CardHeader className="py-2">
              <CardTitle className="text-xs text-muted-foreground">Recent Runs</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[200px]">
                <div className="p-2 space-y-1">
                  {runs.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No runs yet. Upload a PDF or discover assets to get started.
                    </p>
                  ) : (
                    runs.map((run) => (
                      <div key={run.id}>
                        <div
                          className={`p-2 rounded-md cursor-pointer transition-colors ${
                            selectedRunId === run.id ? 'bg-primary/10' : 'hover:bg-muted'
                          }`}
                          onClick={() => {
                            setSelectedRunId(run.id);
                            setSelectedPageNumber(null);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(run.status)}
                              <span className="font-medium text-sm">{run.merchant_name}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Badge variant="secondary" className="text-xs">
                                {run.primary_ocr_engine}
                              </Badge>
                              {run.ai_page_scan_enabled && (
                                <Badge variant="secondary" className="text-xs">
                                  AI
                                </Badge>
                              )}
                              <span>{new Date(run.created_at).toLocaleDateString()}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRunToDelete(run.id);
                                  setShowDeleteDialog(true);
                                }}
                                disabled={deletingRunId === run.id}
                              >
                                {deletingRunId === run.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3 w-3" />
                                )}
                              </Button>
                            </div>
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground flex gap-4">
                            <span>Pages: {run.pages_detected}</span>
                            <span>OCR: {run.pages_ocr_completed}</span>
                            <span>Candidates: {run.pages_candidate}</span>
                            <span>AI: {run.pages_ai_scanned}</span>
                            <span className="text-primary font-medium">
                              Deals: {run.deals_detected_count}
                            </span>
                          </div>
                        </div>
                        {/* CR070: Show error card for failed runs */}
                        {selectedRunId === run.id && <RunErrorCard run={run} />}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Page Grid for Selected Run */}
          {selectedRunId && pages.length > 0 && (
            <Card>
              <CardHeader className="py-2">
                <CardTitle className="text-xs text-muted-foreground">
                  Pages ({pages.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedPageNumber === null ? (
                  <PageListView
                    pages={pages}
                    onSelectPage={(pageNum) => setSelectedPageNumber(pageNum)}
                  />
                ) : (
                  <PageDetailView
                    runId={selectedRunId}
                    pageNumber={selectedPageNumber}
                    deals={deals}
                    onBack={() => setSelectedPageNumber(null)}
                  />
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="deals" className="space-y-4">
          {deals.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">
                  No deals detected yet. Run a scan to find deals.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {deals.map((deal) => (
                <DealGiftCard
                  key={deal.deal_id}
                  deal={mapNormalizedDealToUIDeal(deal, getRunContext())}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="unclassified" className="space-y-4">
          <UnclassifiedQueue
            deals={deals.filter((d) => d.deal_type === 'UNCLASSIFIED')}
            onUpdate={() => {
              if (selectedRunId) {
                loadDeals(selectedRunId);
              }
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Run</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this run and all associated pages, deals, and storage assets. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runToDelete && handleDeleteRun(runToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
